import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ServicePage.css';
import { setPageMeta } from '../utils/seo';

const ExecutiveSearch = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      'Executive Search | HRCONSOL',
      'C-level and leadership hiring with confidential search, market mapping, and competency-based assessment. Build high-impact leadership teams.'
    );
  }, []);

  return (
    <div className="service-page">
      <div className="service-hero">
        <div className="container">
          <h1>Executive Search – Building Leadership That Drives Growth</h1>
          <p className="subtitle">At HRCONSOL, we are a leading Executive Search and Recruitment Firm in Pakistan, connecting organizations with visionary leaders who deliver real business impact.</p>
        </div>
      </div>

      <div className="container">
        <div className="service-content">
          <div className="service-section">
            <p>We specialize in <strong>C-level hiring, leadership recruitment, and headhunting services</strong> for corporates, multinationals, and startups across Pakistan, MENA, and Africa.</p>
            <p>Whether you're scaling your business or restructuring your leadership, HRCONSOL Executive Search ensures you hire the right leaders to accelerate performance, innovation, and growth.</p>
          </div>

          <div className="service-section">
            <h2>Scope of Services</h2>
            <p>Our Premium Executive Search & Recruitment Solutions include:</p>
            <div className="process-steps">
              <div className="step">
                <h3><i className="fas fa-users"></i> C-Level & Leadership Hiring</h3>
                <p>CEOs, CFOs, CHROs, CMOs, CTOs, and functional heads.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-lock"></i> Confidential Search</h3>
                <p>Discreet hiring for high-level and sensitive positions.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-chart-line"></i> Market Mapping</h3>
                <p>Deep talent intelligence and competitor benchmarking.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-clipboard-check"></i> Competency-Based Assessment</h3>
                <p>Evaluate leadership potential, culture fit, and succession readiness.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-globe"></i> Global Talent Reach</h3>
                <p>Access to regional and international leadership networks.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-user-tie"></i> Dedicated Account Manager</h3>
                <p>Single point of contact for every client.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-rocket"></i> Startup Recruitment</h3>
                <p>Agile hiring models for fast-growing ventures.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-tasks"></i> End-to-End Management</h3>
                <p>From candidate sourcing to onboarding and retention.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Industries We Serve</h2>
            <p>As one of Pakistan's top executive recruitment agencies, HRCONSOL serves a wide range of industries, including:</p>
            <ul className="benefits-list">
              <li><i className="fas fa-cog"></i> Manufacturing & Industrial</li>
              <li><i className="fas fa-gas-pump"></i> Oil, Gas & Energy</li>
              <li><i className="fas fa-tools"></i> Engineering & Infrastructure</li>
              <li><i className="fas fa-truck"></i> Logistics, Supply Chain & Transportation</li>
              <li><i className="fas fa-university"></i> Banking, Financial Services & Insurance (BFSI)</li>
              <li><i className="fas fa-pills"></i> Pharmaceuticals & Healthcare</li>
              <li><i className="fas fa-shopping-cart"></i> FMCG, Retail & Consumer Goods</li>
              <li><i className="fas fa-laptop-code"></i> Information Technology, Software & E-Commerce</li>
              <li><i className="fas fa-graduation-cap"></i> Education & Training</li>
              <li><i className="fas fa-building"></i> Real Estate, Facility Management & Construction</li>
              <li><i className="fas fa-leaf"></i> Development, NGO & Sustainability Projects</li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Startup & Emerging Ventures</h2>
            <p>Our <strong>Startup Recruitment Services</strong> are designed to help new ventures build founding teams and leadership pipelines that scale.</p>
            <p>We've helped startups across multiple industries attract investors, innovate faster, and strengthen their leadership base.</p>
            <p>We specialize in executive and mid-level hiring for:</p>
            <div className="process-steps">
              <div className="step">
                <h3><i className="fas fa-coins"></i> Fintech</h3>
              </div>
              <div className="step">
                <h3><i className="fas fa-heartbeat"></i> Healthtech</h3>
              </div>
              <div className="step">
                <h3><i className="fas fa-book-reader"></i> Edtech</h3>
              </div>
              <div className="step">
                <h3><i className="fas fa-utensils"></i> Foodtech</h3>
              </div>
              <div className="step">
                <h3><i className="fas fa-shopping-bag"></i> E-Commerce</h3>
              </div>
              <div className="step">
                <h3><i className="fas fa-route"></i> Logistics & Mobility</h3>
              </div>
              <div className="step">
                <h3><i className="fas fa-lightbulb"></i> Green Energy & Sustainability</h3>
              </div>
              <div className="step">
                <h3><i className="fas fa-brain"></i> AI, SaaS & Technology Ventures</h3>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Why Choose HRCONSOL Executive Search?</h2>
            <p className="track-record-intro"><strong>Our Track Record Speaks for Itself:</strong></p>
            <ul className="benefits-list">
              <li>
                <span className="benefit-heading">4,500+ Positions Closed</span>
                <span className="benefit-subheading">across 50+ industries</span>
              </li>
              <li>
                <span className="benefit-heading">95% Client Retention Rate</span>
                <span className="benefit-subheading">through consistent delivery and partnership</span>
              </li>
              <li>
                <span className="benefit-heading">40% Faster Hiring Time</span>
                <span className="benefit-subheading">with AI-enabled HRMS and talent mapping tools</span>
              </li>
              <li>
                <span className="benefit-heading">85% Hires Retained Beyond One Year</span>
                <span className="benefit-subheading">– ensuring quality and stability</span>
              </li>
              <li>
                <span className="benefit-heading">4.9/5 Client Satisfaction Rating</span>
                <span className="benefit-subheading">(based on post-placement surveys)</span>
              </li>
              <li>
                <span className="benefit-heading">30+ Startups & Scaleups Supported</span>
                <span className="benefit-subheading">in building leadership and tech teams</span>
              </li>
            </ul>
            
            <div className="service-cta" style={{ marginTop: '2rem', background: '#000' }}>
              <p>Companies that partnered with HRCONSOL reported</p>
              <ul className="benefits-list" style={{ gridTemplateColumns: '1fr 1fr', marginTop: '1rem' }}>
                <li><strong>30–40% improvement</strong> in team performance</li>
                <li>Reduction in <strong>cost-per-hire by up to 35%</strong></li>
              </ul>
            </div>
          </div>

          <div className="service-section">
            <h2>Packages Details</h2>
            
            <div className="process-steps">
              <div className="step package-card">
                <h3>1. STARTER</h3>
                <p>Startups & Small Businesses (Up to 5 Positions)</p>
                <ul className="package-list">
                  <li>Fee: 1 Gross Monthly Salary per position</li>
                  <li>Discount: 10% OFF on total invoice</li>
                  <li>Replacement Guarantee: Within 30 days</li>
                  <li>Add-On: Free job branding on HRCONSOL network</li>
                </ul>
                <p className="package-note">Perfect for emerging businesses making their first key hires.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>2. GROWTH</h3>
                <p>Growing Companies (6 to 25 Positions)</p>
                <ul className="package-list">
                  <li>Fee: 75% of Gross Monthly Salary per hire</li>
                  <li>Discount: 20% OFF on total project cost</li>
                  <li>Replacement Guarantee: Within 30 days</li>
                  <li>Add-On: Free recruitment analytics & progress dashboard</li>
                </ul>
                <p className="package-note">Ideal for mid-size organizations expanding operations or building regional teams.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>3. ELITE</h3>
                <p>Large Enterprises (More than 26 Positions)</p>
                <ul className="package-list">
                  <li>Fee: 70% of Gross Monthly Salary per hire</li>
                  <li>Discount: 25% OFF on bulk hiring</li>
                  <li>Replacement Guarantee: Within 30 days</li>
                  <li>Add-On: Dedicated Account Manager + Monthly Hiring Reports</li>
                </ul>
                <p className="package-note">Perfect for established companies looking for long-term manpower scalability.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>4. ENTERPRISE+</h3>
                <p>High-Volume / Ongoing Recruitment (Minimum 50 Positions)</p>
                <ul className="package-list">
                  <li>Fee: 60% of Gross Monthly Salary per hire</li>
                  <li>Discount: Up to 40% OFF on annual partnership model</li>
                  <li>Replacement Guarantee: Within 30 days</li>
                  <li>Add-Ons:</li>
                  <li className="package-sublist">- Full RPO support (Recruitment Process Outsourcing)</li>
                  <li className="package-sublist">- Employer branding consultation (worth PKR 90,000)</li>
                  <li className="package-sublist">- Access to HRCONSOL HRMS dashboard</li>
                </ul>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>
            </div>
          </div>

          <div className="service-cta">
            <h2>Ready to Build Your Leadership Team?</h2>
            <p>Let's discuss your executive search needs and find the perfect leaders for your organization.</p>
            <button onClick={() => navigate('/#contact')} className="cta-button">Contact Us</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSearch;
